import { formatIsoDate } from "./agendaCalendarStorage";
import { parseTimeToMinutes } from "./agendaDateTime";
import type { AgendaGridTimeBounds } from "./agendaGridTypes";

export const AGENDA_SLOT_MINUTES = 15;
export const AGENDA_SLOT_HEIGHT_PX = 20;

export type GridPosition = {
  topPercent: number;
  heightPercent: number;
};

export const parseDatetimeToMinutes = (datetime: string): number => {
  const normalized = datetime.trim().replace(" ", "T");
  const timePart = normalized.slice(11, 19);

  return parseTimeToMinutes(timePart || "00:00:00");
};

export const getGridBoundsMinutes = (bounds: AgendaGridTimeBounds): {
  start: number;
  end: number;
  total: number;
} => {
  const start = parseTimeToMinutes(bounds.slotMinTime);
  const end = parseTimeToMinutes(bounds.slotMaxTime);

  return {
    start,
    end,
    total: Math.max(end - start, AGENDA_SLOT_MINUTES),
  };
};

export const computeGridPosition = (
  startDatetime: string,
  endDatetime: string,
  bounds: AgendaGridTimeBounds
): GridPosition | null => {
  const { start: gridStart, total } = getGridBoundsMinutes(bounds);
  const eventStart = parseDatetimeToMinutes(startDatetime);
  const eventEnd = parseDatetimeToMinutes(endDatetime);
  const gridEnd = gridStart + total;

  if (eventEnd <= gridStart || eventStart >= gridEnd) {
    return null;
  }

  const clampedStart = Math.max(eventStart, gridStart);
  const clampedEnd = Math.min(eventEnd, gridEnd);
  const topPercent = ((clampedStart - gridStart) / total) * 100;
  const heightPercent = ((clampedEnd - clampedStart) / total) * 100;

  if (heightPercent <= 0) {
    return null;
  }

  return {
    topPercent,
    heightPercent,
  };
};

export const snapMinutesToSlot = (
  minutes: number,
  slotMinutes: number = AGENDA_SLOT_MINUTES
): number => Math.round(minutes / slotMinutes) * slotMinutes;

export const clampMinutesToBounds = (
  minutes: number,
  bounds: AgendaGridTimeBounds
): number => {
  const { start, end } = getGridBoundsMinutes(bounds);

  return Math.min(Math.max(minutes, start), end - AGENDA_SLOT_MINUTES);
};

export const minutesFromPointerY = (
  clientY: number,
  columnRect: DOMRect,
  bounds: AgendaGridTimeBounds
): number => {
  const { start: gridStart, total } = getGridBoundsMinutes(bounds);
  const ratio = Math.min(1, Math.max(0, (clientY - columnRect.top) / columnRect.height));
  const rawMinutes = gridStart + ratio * total;

  return clampMinutesToBounds(snapMinutesToSlot(rawMinutes), bounds);
};

export const buildHourSlotLabels = (
  bounds: AgendaGridTimeBounds,
  stepMinutes: number = 60
): Array<{ minutes: number; label: string }> => {
  const { start, end } = getGridBoundsMinutes(bounds);
  const labels: Array<{ minutes: number; label: string }> = [];

  for (let minutes = start; minutes < end; minutes += stepMinutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    labels.push({
      minutes,
      label: mins === 0 ? `${String(hours).padStart(2, "0")}h` : `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`,
    });
  }

  return labels;
};

export const countGridSlots = (bounds: AgendaGridTimeBounds): number => {
  const { total } = getGridBoundsMinutes(bounds);

  return Math.ceil(total / AGENDA_SLOT_MINUTES);
};

export const addDaysToIsoDate = (isoDate: string, delta: number): string => {
  const next = new Date(`${isoDate}T12:00:00`);
  next.setDate(next.getDate() + delta);

  return formatIsoDate(next);
};

export const minutesToWallClockDatetime = (date: string, minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return `${date} ${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:00`;
};

export const formatCalendarNavTitle = (
  selectedDate: string,
  locale: string,
  weekMode: boolean
): string => {
  const anchor = new Date(`${selectedDate}T12:00:00`);

  if (!weekMode) {
    return new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(anchor);
  }

  const day = anchor.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const weekStart = new Date(anchor);
  weekStart.setDate(anchor.getDate() + diffToMonday);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
  const sameYear = weekStart.getFullYear() === weekEnd.getFullYear();

  const startLabel = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: sameMonth ? undefined : "short",
    year: sameYear ? undefined : "numeric",
  }).format(weekStart);

  const endLabel = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(weekEnd);

  return `${startLabel} – ${endLabel}`;
};

export const computeNowIndicatorPercent = (
  bounds: AgendaGridTimeBounds,
  now: Date = new Date()
): number | null => {
  const { start: gridStart, total } = getGridBoundsMinutes(bounds);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  if (nowMinutes < gridStart || nowMinutes > gridStart + total) {
    return null;
  }

  return ((nowMinutes - gridStart) / total) * 100;
};

export const timeRangesOverlap = (
  startA: number,
  endA: number,
  startB: number,
  endB: number
): boolean => startA < endB && startB < endA;

export type DropPreviewState = {
  columnId: string;
  topPercent: number;
  heightPercent: number;
  hasConflict: boolean;
};

export const computeDropPreview = (
  clientY: number,
  columnRect: DOMRect,
  columnId: string,
  durationMinutes: number,
  excludeAppointmentId: number,
  columnBlocks: Array<{ kind: string; appointment: { id: number }; startDatetime: string; endDatetime: string }>,
  bounds: AgendaGridTimeBounds
): DropPreviewState => {
  const { start: gridStart, end: gridEnd, total } = getGridBoundsMinutes(bounds);
  let startMinutes = minutesFromPointerY(clientY, columnRect, bounds);

  startMinutes = Math.min(startMinutes, gridEnd - durationMinutes);
  const endMinutes = startMinutes + durationMinutes;
  const topPercent = ((startMinutes - gridStart) / total) * 100;
  const heightPercent = (durationMinutes / total) * 100;

  const hasConflict = columnBlocks.some((block) => {
    if (block.kind !== "appointment" || block.appointment.id === excludeAppointmentId) {
      return false;
    }

    const blockStart = parseDatetimeToMinutes(block.startDatetime);
    const blockEnd = parseDatetimeToMinutes(block.endDatetime);

    return timeRangesOverlap(startMinutes, endMinutes, blockStart, blockEnd);
  });

  return {
    columnId,
    topPercent,
    heightPercent,
    hasConflict,
  };
};
